import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitcher from '../common/LanguageSwitcher';
import UserAvatar from '../common/UserAvatar';
import '../features/FreelancerProfile.css';

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

export default function FreelancerPublicProfile() {
  const { freelancerId } = useParams();
  const { currentUser } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!freelancerId) return;

    const loadProfile = async () => {
      try {
        const token = currentUser ? await currentUser.getIdToken() : null;
        const res = await fetch(
          `${API_BASE_URL}/api/freelancer/profile/${freelancerId}/public`,
          {
            headers: token
              ? {
                  Authorization: `Bearer ${token}`
                }
              : {}
          }
        );
        if (!res.ok) {
          throw new Error('Failed to load freelancer profile');
        }
        const json = await res.json();
        setProfile(json);
      } catch (e) {
        console.error('Failed to load public freelancer profile', e);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [freelancerId, currentUser]);

  if (loading) {
    return (
      <div className="freelancer-profile public">
        <div className="freelancer-dashboard-loading">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="freelancer-profile public">
        <div className="freelancer-dashboard-loading">
          {t('ratings.enterRating') || 'Freelancer not found'}
        </div>
      </div>
    );
  }

  const displayName =
    profile.displayName ||
    profile.name ||
    profile.email?.split('@')[0] ||
    'Freelancer';

  const ratingValue =
    typeof profile.rating === 'number'
      ? profile.rating.toFixed(1)
      : typeof profile.rating_overall === 'number'
      ? profile.rating_overall.toFixed(1)
      : '3.0';

  const jobsCompleted =
    typeof profile.jobsCompleted === 'number' ? profile.jobsCompleted : 0;

  const level =
    typeof profile.level === 'number'
      ? profile.level
      : typeof profile.freelancerLevel === 'number'
      ? profile.freelancerLevel
      : 1;

  const xp = typeof profile.xp === 'number' ? profile.xp : 0;
  const levelProgress = { currentXp: xp, maxXp: 3000 };
  const progressPercent = Math.min(
    100,
    Math.round((levelProgress.currentXp / levelProgress.maxXp) * 100)
  );

  return (
    <div
      className={`freelancer-profile ${isRTL ? 'rtl' : ''} public`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <header className="freelancer-dashboard-header">
        <div className="freelancer-dashboard-header-content">
          <LanguageSwitcher />
          <button
            className="business-public-back"
            onClick={() => navigate(-1)}
          >
            {t('common.back') || 'Back'}
          </button>
        </div>
      </header>

      <div className="freelancer-grid">
        <div className="freelancer-main-card">
          <div className="freelancer-card-header">
            <div className="freelancer-user">
              <UserAvatar
                user={{ ...profile, uid: freelancerId, photoURL: profile.photoURL }}
                size="xl"
              />
              <div className="freelancer-user-info">
                <div className="freelancer-user-top">
                  <h2 className="freelancer-name">{displayName}</h2>
                  <span className="freelancer-badge">Freelancer</span>
                </div>
                <div className="freelancer-inline-stats">
                  <span>⚡ Level {level}</span>
                  <span>📈 {jobsCompleted} Jobs</span>
                  <span>⭐ {ratingValue}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="freelancer-progress">
            <div className="freelancer-progress-label">Level Progress</div>
            <div className="freelancer-progress-bar">
              <div
                className="freelancer-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="freelancer-progress-value">
              {levelProgress.currentXp} / {levelProgress.maxXp} XP
            </div>
          </div>

          <div className="freelancer-section">
            <h3>{t('profile.about') || 'About'}</h3>
            <p>
              {profile.about ||
                t('profile.noAbout') ||
                'This freelancer has not added an about section yet.'}
            </p>
          </div>

          <div className="freelancer-section">
            <h3>{t('jobs.description') || 'Description'}</h3>
            <p>{profile.short_description || profile.bio || '—'}</p>
          </div>

          <div className="freelancer-section">
            <h3>{t('jobs.location') || 'Location'}</h3>
            <p>{profile.location || '—'}</p>
          </div>

          <div className="freelancer-section">
            <h3>{t('jobs.requirements') || 'Skills'}</h3>
            {Array.isArray(profile.skills) && profile.skills.length > 0 ? (
              <ul className="detail-section-list">
                {profile.skills.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            ) : (
              <p>—</p>
            )}
          </div>
        </div>

        <aside className="freelancer-side-card">
          <div className="freelancer-quick-title">Quick Stats</div>
          <div className="freelancer-quick-row">
            <div className="freelancer-quick-label">Total Jobs</div>
            <div className="freelancer-quick-value">{jobsCompleted}</div>
          </div>
          <div className="freelancer-quick-row">
            <div className="freelancer-quick-label">Rating</div>
            <div className="freelancer-quick-value">{ratingValue} ★</div>
          </div>
        </aside>
      </div>
    </div>
  );
}


